import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FaHome, FaBoxOpen, FaEnvelope, FaBars, FaTimes, FaChartBar, FaPlus } from "react-icons/fa";
import { motion, AnimatePresence } from 'framer-motion';
import './NavBar.css';
import CardWidget from '../cardWidget/CardWidget';
import Login from '../Login';
import { useAuth } from "../../context/AuthContext";
import { isAdminUser } from "../../config/adminConfig";

const NavBar = (props) => {
  const [isOpen, setIsOpen] = useState(false);
  const { user } = useAuth();
  const location = useLocation();
  const isAdmin = isAdminUser(user);

  const toggleMenu = () => setIsOpen(!isOpen);
  const closeMenu = () => setIsOpen(false);

  const navLinks = [
    { path: '/', label: 'Home', icon: <FaHome /> },
    { path: '/productos#categorias', label: 'Productos', icon: <FaBoxOpen /> },
    { path: '/contacto', label: 'Contacto', icon: <FaEnvelope /> },
  ];

  const menuVariants = {
    closed: { x: '100%', opacity: 0 },
    open: { x: 0, opacity: 1, transition: { type: 'spring', stiffness: 300, damping: 30 } }
  };

  const linkVariants = {
    closed: { x: 50, opacity: 0 },
    open: i => ({ x: 0, opacity: 1, transition: { delay: i * 0.1 } })
  };

  return (
    <header className="cyber-header">
      <nav className="cyber-navbar">
        {/* Logo */}
        <Link to="/" className="cyber-brand" onClick={closeMenu}>
          <motion.div 
            className="brand-logo-wrap"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <img src="/images/logoWeb.webp?v=2" alt="Techteno" className="cyber-logo" />
            <span className="cyber-brand-text">Techteno</span>
          </motion.div>
        </Link>

        {/* Desktop Navigation */}
        <div className="cyber-nav-desktop">
          {navLinks.map((link) => (
            <Link 
              key={link.path}
              to={link.path}
              className={`cyber-nav-link ${location.pathname === link.path ? 'active' : ''}`}
            >
              <span className="nav-link-text">{link.label}</span>
              <span className="nav-link-glow"></span>
            </Link>
          ))}
        </div>

        {/* Actions */}
        <div className="cyber-nav-actions">
          {isAdmin && (
            <Link to="/admin/kits" className="admin-link admin-link-kits" title="Agregar / gestionar kits">
              <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                <FaPlus />
              </motion.div>
            </Link>
          )}
          {isAdmin && (
            <Link to="/admin" className="admin-link" title="Panel Admin">
              <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                <FaChartBar />
              </motion.div>
            </Link>
          )}
          {user && (
            <Link to="/carrito#inicioCarrito" onClick={closeMenu}>
              <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                <CardWidget />
              </motion.div>
            </Link>
          )}
          <Login />
          
          {/* Mobile Toggle */}
          <motion.button 
            className="cyber-menu-toggle"
            onClick={toggleMenu}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            {isOpen ? <FaTimes /> : <FaBars />}
          </motion.button>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isOpen && (
            <>
              <motion.div 
                className="cyber-menu-overlay"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={closeMenu}
              />
              <motion.div 
                className="cyber-mobile-menu"
                variants={menuVariants}
                initial="closed"
                animate="open"
                exit="closed"
              >
                <div className="mobile-menu-header">
                  <span className="mobile-menu-title">Menú</span>
                  <motion.button 
                    className="mobile-close-btn"
                    onClick={closeMenu}
                    whileHover={{ scale: 1.1, rotate: 90 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <FaTimes />
                  </motion.button>
                </div>
                
                <div className="mobile-nav-links">
                  {navLinks.map((link, i) => (
                    <motion.div
                      key={link.path}
                      custom={i}
                      variants={linkVariants}
                      initial="closed"
                      animate="open"
                    >
                      <Link 
                        to={link.path}
                        className="mobile-nav-link"
                        onClick={closeMenu}
                      >
                        <span className="mobile-link-icon">{link.icon}</span>
                        <span className="mobile-link-text">{link.label}</span>
                        <span className="mobile-link-arrow">→</span>
                      </Link>
                    </motion.div>
                  ))}
                  {isAdmin && (
                    <motion.div
                      custom={navLinks.length}
                      variants={linkVariants}
                      initial="closed"
                      animate="open"
                    >
                      <Link 
                        to="/admin"
                        className="mobile-nav-link admin-mobile-link"
                        onClick={closeMenu}
                      >
                        <span className="mobile-link-icon"><FaChartBar /></span>
                        <span className="mobile-link-text">Panel Admin</span>
                        <span className="mobile-link-arrow">→</span>
                      </Link>
                    </motion.div>
                  )}
                  {isAdmin && (
                    <motion.div
                      custom={navLinks.length + 1}
                      variants={linkVariants}
                      initial="closed"
                      animate="open"
                    >
                      <Link 
                        to="/admin/kits"
                        className="mobile-nav-link admin-mobile-link"
                        onClick={closeMenu}
                      >
                        <span className="mobile-link-icon"><FaPlus /></span>
                        <span className="mobile-link-text">Agregar kit</span>
                        <span className="mobile-link-arrow">→</span>
                      </Link>
                    </motion.div>
                  )}
                </div>

                <div className="mobile-menu-footer">
                  <p>Tu tienda de tecnología de confianza</p>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </nav>
    </header>
  );
};

export default NavBar;
